import React, { useState, useEffect } from 'react';
import Page from "../../components/Page";
import { useTranslation } from 'react-i18next';
import { useWebsiteSettings, updateWebsiteSettings, useWebsitePackages, addWebsitePackage, updateWebsitePackage, deleteWebsitePackage, useWebsiteProducts, addWebsiteProduct, updateWebsiteProduct, deleteWebsiteProduct, useWebsiteStories, addWebsiteStory, updateWebsiteStory, deleteWebsiteStory, useWebsiteReviews, addWebsiteReview, updateWebsiteReview, deleteWebsiteReview, googleDriveToDirectUrl } from '../../controllers/website_crm.controller';
import toast from 'react-hot-toast';
import { IconSettings, IconPackage, IconTemplate, IconVideo, IconStar, IconEdit, IconTrash, IconPlus, IconExternalLink } from '@tabler/icons-react';
import { iconStroke } from '../../config/config';
import AppLinksManager from '../../components/SuperAdmin/AppLinksManager';

export default function WebsiteCRMPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('settings');

  return (
    <Page className="px-4 py-3 overflow-x-hidden h-full flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800">Website CRM</h3>
        <a 
          href="/website" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-xl transition-all"
        >
          <IconExternalLink size={20} />
          View Live Website
        </a>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 bg-gray-100 p-2 rounded-2xl w-fit">
        <button onClick={() => setActiveTab('settings')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === 'settings' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:bg-gray-200'}`}>
          <IconSettings size={18} stroke={iconStroke} /> Settings
        </button>
        <button onClick={() => setActiveTab('packages')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === 'packages' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:bg-gray-200'}`}>
          <IconPackage size={18} stroke={iconStroke} /> Packages
        </button>
        <button onClick={() => setActiveTab('products')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === 'products' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:bg-gray-200'}`}>
          <IconTemplate size={18} stroke={iconStroke} /> Products
        </button>
        <button onClick={() => setActiveTab('stories')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === 'stories' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:bg-gray-200'}`}>
          <IconVideo size={18} stroke={iconStroke} /> Stories
        </button>
        <button onClick={() => setActiveTab('reviews')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === 'reviews' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:bg-gray-200'}`}>
          <IconStar size={18} stroke={iconStroke} /> Reviews
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pb-20">
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'packages' && <PackagesTab />}
        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'stories' && <StoriesTab />}
        {activeTab === 'reviews' && <ReviewsTab />}
      </div>
    </Page>
  );
}

function SettingsTab() {
  const { data, isLoading, mutate } = useWebsiteSettings();
  const [formData, setFormData] = useState({
    site_name: '', logo_url: '', tagline: '', hero_title: '', hero_subtitle: '', hero_image_url: '', primary_color: '#16a34a', secondary_color: '#1e40af', phone: '', email: '', address: '', map_link: '', whatsapp: '', facebook_url: '', instagram_url: '',
  });
  const [appLinks, setAppLinks] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        site_name: data.site_name || '', logo_url: data.logo_url || '', tagline: data.tagline || '', hero_title: data.hero_title || '', hero_subtitle: data.hero_subtitle || '', hero_image_url: data.hero_image_url || '', primary_color: data.primary_color || '#16a34a', secondary_color: data.secondary_color || '#1e40af', phone: data.phone || '', email: data.email || '', address: data.address || '', map_link: data.map_link || '', whatsapp: data.whatsapp || '', facebook_url: data.facebook_url || '', instagram_url: data.instagram_url || '',
      });

      let parsedLinks = [];
      if (data.appLinks) {
        parsedLinks = data.appLinks;
      } else if (data.APP_DOWNLOAD_LINKS) {
        try {
          parsedLinks = typeof data.APP_DOWNLOAD_LINKS === 'string' ? JSON.parse(data.APP_DOWNLOAD_LINKS) : data.APP_DOWNLOAD_LINKS;
        } catch(e) {
          parsedLinks = [];
        }
      }
      if (!Array.isArray(parsedLinks) || parsedLinks.length === 0) {
        parsedLinks = [
          { id: "captain_app", name: "Captain App", download_link: data.CAPTAIN_APP_DOWNLOAD_LINK || "", manual_link: data.CAPTAIN_APP_MANUAL_LINK || "" },
          { id: "waiter_app", name: "Waiter App", download_link: data.WAITER_APP_DOWNLOAD_LINK || "", manual_link: data.WAITER_APP_MANUAL_LINK || "" },
          { id: "printer_driver", name: "Printer Driver", download_link: data.PRINTER_DRIVER_DOWNLOAD_LINK || "", manual_link: data.PRINTER_DRIVER_MANUAL_LINK || "" },
          { id: "windows_app", name: "Windows App", download_link: data.WINDOWS_APP_DOWNLOAD_LINK || "", manual_link: data.WINDOWS_APP_MANUAL_LINK || "" },
          { id: "linux_app", name: "Linux App", download_link: data.LINUX_APP_DOWNLOAD_LINK || "", manual_link: data.LINUX_APP_MANUAL_LINK || "" },
          { id: "mac_app", name: "Mac App", download_link: data.MAC_APP_DOWNLOAD_LINK || "", manual_link: data.MAC_APP_MANUAL_LINK || "" }
        ];
      }
      setAppLinks(parsedLinks);
    }
  }, [data]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const payload = {
        ...formData,
        APP_DOWNLOAD_LINKS: JSON.stringify(appLinks)
      };
      await updateWebsiteSettings(payload);
      toast.success('Settings saved successfully');
      mutate();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div>Loading settings...</div>;

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 max-w-4xl">
      <h4 className="text-lg font-bold mb-6 text-gray-800">General Settings</h4>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
          <input type="text" name="site_name" value={formData.site_name} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (Google Drive Link)</label>
          <input type="text" name="logo_url" value={formData.logo_url} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" placeholder="https://drive.google.com/file/d/..." />
          {formData.logo_url && (
            <div className="mt-2 text-sm text-gray-500 flex items-center gap-2">
              <span>Preview:</span>
              <img src={googleDriveToDirectUrl(formData.logo_url)} alt="Logo Preview" className="h-8 object-contain bg-gray-100 p-1 rounded" onError={(e) => e.target.style.display = 'none'} />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
          <div className="flex gap-2">
            <input type="color" name="primary_color" value={formData.primary_color} onChange={handleChange} className="h-10 w-10 rounded border" />
            <input type="text" name="primary_color" value={formData.primary_color} onChange={handleChange} className="flex-1 border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
          <div className="flex gap-2">
            <input type="color" name="secondary_color" value={formData.secondary_color} onChange={handleChange} className="h-10 w-10 rounded border" />
            <input type="text" name="secondary_color" value={formData.secondary_color} onChange={handleChange} className="flex-1 border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
          </div>
        </div>
      </div>

      <h4 className="text-lg font-bold mb-4 mt-8 text-gray-800">Hero Section</h4>
      <div className="grid grid-cols-1 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
          <input type="text" name="tagline" value={formData.tagline} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hero Title</label>
          <input type="text" name="hero_title" value={formData.hero_title} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hero Subtitle</label>
          <textarea name="hero_subtitle" value={formData.hero_subtitle} onChange={handleChange} rows="3" className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none"></textarea>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hero Image URL (Google Drive Link)</label>
          <input type="text" name="hero_image_url" value={formData.hero_image_url} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" placeholder="https://drive.google.com/file/d/..." />
        </div>
      </div>

      <h4 className="text-lg font-bold mb-4 mt-8 text-gray-800">Contact Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input type="text" name="phone" value={formData.phone} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
          <input type="text" name="whatsapp" value={formData.whatsapp} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Google Maps Link</label>
          <input type="text" name="map_link" value={formData.map_link} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" placeholder="https://maps.google.com/..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
          <input type="text" name="facebook_url" value={formData.facebook_url} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL</label>
          <input type="text" name="instagram_url" value={formData.instagram_url} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none" />
        </div>
      </div>

      <div className="mt-8 border-t pt-8">
        <AppLinksManager links={appLinks} onChange={setAppLinks} />
      </div>

      <div className="flex justify-end mt-8">
        <button type="submit" disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-2 rounded-xl transition-all flex items-center gap-2 shadow-sm">
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </form>
  );
}

function PackagesTab() {
  const { data: packages, isLoading, mutate } = useWebsitePackages();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const initialForm = { name: '', price: '', duration: 'monthly', features: '', is_active: 1, is_popular: 0 };
  const [formData, setFormData] = useState(initialForm);

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData(initialForm);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateWebsitePackage(editingItem.id, formData);
        toast.success('Package updated');
      } else {
        await addWebsitePackage(formData);
        toast.success('Package added');
      }
      setShowModal(false);
      mutate();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        await deleteWebsitePackage(id);
        toast.success('Package deleted');
        mutate();
      } catch (error) {
        toast.error('Delete failed');
      }
    }
  };

  if (isLoading) return <div>Loading packages...</div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => openModal()} className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-xl flex items-center gap-2">
          <IconPlus size={18} /> Add Package
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages?.map(pkg => (
          <div key={pkg.id} className={`bg-white rounded-2xl shadow-sm border p-6 ${pkg.is_popular ? 'border-green-400' : 'border-gray-200'}`}>
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-xl font-bold text-gray-800">{pkg.name}</h4>
              <div className="flex gap-2">
                <button onClick={() => openModal(pkg)} className="text-blue-500 hover:bg-blue-50 p-1 rounded"><IconEdit size={18} /></button>
                <button onClick={() => handleDelete(pkg.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><IconTrash size={18} /></button>
              </div>
            </div>
            <div className="text-3xl font-black text-gray-900 mb-2">
              {pkg.currency}{pkg.price} <span className="text-sm font-normal text-gray-500">/{pkg.duration}</span>
            </div>
            <p className="text-sm text-gray-500 mb-4 whitespace-pre-line">{pkg.features}</p>
            <div className="flex gap-2 text-xs">
              <span className={`px-2 py-1 rounded-full ${pkg.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {pkg.is_active ? 'Active' : 'Inactive'}
              </span>
              {pkg.is_popular === 1 && <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700">Popular</span>}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Edit Package' : 'Add Package'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                    <input type="text" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" placeholder="e.g. monthly, year" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
                  <textarea value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})} rows="4" className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500"></textarea>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})} className="rounded text-green-600 focus:ring-green-500 h-4 w-4" />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_popular} onChange={(e) => setFormData({...formData, is_popular: e.target.checked ? 1 : 0})} className="rounded text-green-600 focus:ring-green-500 h-4 w-4" />
                    <span>Mark as Popular</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductsTab() {
  const { data: products, isLoading, mutate } = useWebsiteProducts();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const initialForm = { name: '', description: '', price: '', image_url: '', is_active: 1 };
  const [formData, setFormData] = useState(initialForm);

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData(initialForm);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateWebsiteProduct(editingItem.id, formData);
        toast.success('Product updated');
      } else {
        await addWebsiteProduct(formData);
        toast.success('Product added');
      }
      setShowModal(false);
      mutate();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteWebsiteProduct(id);
        toast.success('Product deleted');
        mutate();
      } catch (error) {
        toast.error('Delete failed');
      }
    }
  };

  if (isLoading) return <div>Loading products...</div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => openModal()} className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-xl flex items-center gap-2">
          <IconPlus size={18} /> Add Product
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {products?.map(prod => (
          <div key={prod.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {prod.image_url ? (
              <img src={googleDriveToDirectUrl(prod.image_url)} alt={prod.name} className="w-full h-48 object-cover" onError={(e) => {e.target.src = 'https://placehold.co/400x300?text=No+Image'}} />
            ) : (
              <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-400">No Image</div>
            )}
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-lg font-bold text-gray-800 line-clamp-1">{prod.name}</h4>
                <div className="flex gap-1">
                  <button onClick={() => openModal(prod)} className="text-blue-500 hover:bg-blue-50 p-1 rounded"><IconEdit size={16} /></button>
                  <button onClick={() => handleDelete(prod.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><IconTrash size={16} /></button>
                </div>
              </div>
              <div className="text-xl font-black text-green-600 mb-2">रु{prod.price}</div>
              <p className="text-sm text-gray-500 line-clamp-2">{prod.description}</p>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Edit Product' : 'Add Product'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                  <input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Google Drive Image URL</label>
                  <input type="text" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="3" className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500"></textarea>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})} className="rounded text-green-600 focus:ring-green-500 h-4 w-4" />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StoriesTab() {
  const { data: stories, isLoading, mutate } = useWebsiteStories();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const initialForm = { title: '', youtube_url: '', image_url: '', is_active: 1 };
  const [formData, setFormData] = useState(initialForm);

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData(initialForm);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateWebsiteStory(editingItem.id, formData);
        toast.success('Story updated');
      } else {
        await addWebsiteStory(formData);
        toast.success('Story added');
      }
      setShowModal(false);
      mutate();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this story?')) {
      try {
        await deleteWebsiteStory(id);
        toast.success('Story deleted');
        mutate();
      } catch (error) {
        toast.error('Delete failed');
      }
    }
  };

  // Helper to extract YouTube ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&]{11})/);
    return match ? match[1] : null;
  };

  if (isLoading) return <div>Loading stories...</div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => openModal()} className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-xl flex items-center gap-2">
          <IconPlus size={18} /> Add YouTube Story
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stories?.map(story => {
          const ytId = getYouTubeId(story.youtube_url);
          return (
            <div key={story.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="relative w-full aspect-video bg-gray-100 flex items-center justify-center overflow-hidden">
                {ytId ? (
                  <iframe 
                    src={`https://www.youtube.com/embed/${ytId}?controls=0`} 
                    className="w-full h-full" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen>
                  </iframe>
                ) : story.image_url ? (
                  <img src={googleDriveToDirectUrl(story.image_url)} alt={story.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">Invalid Media URL</div>
                )}
              </div>
              <div className="p-4 flex justify-between items-start">
                <h4 className="font-bold text-gray-800 line-clamp-2">{story.title}</h4>
                <div className="flex gap-1 ml-2 shrink-0">
                  <button onClick={() => openModal(story)} className="text-blue-500 hover:bg-blue-50 p-1 rounded"><IconEdit size={16} /></button>
                  <button onClick={() => handleDelete(story.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><IconTrash size={16} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Edit Story' : 'Add YouTube Story'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
                  <input type="url" value={formData.youtube_url || ''} onChange={(e) => setFormData({...formData, youtube_url: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" placeholder="https://www.youtube.com/watch?v=..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">OR Image URL (Google Drive Link)</label>
                  <input type="text" value={formData.image_url || ''} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" placeholder="https://drive.google.com/file/d/..." />
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})} className="rounded text-green-600 focus:ring-green-500 h-4 w-4" />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewsTab() {
  const { data: reviews, isLoading, mutate } = useWebsiteReviews();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const initialForm = { customer_name: '', customer_photo_url: '', rating: 5, comment: '', designation: '', is_active: 1 };
  const [formData, setFormData] = useState(initialForm);

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData(initialForm);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateWebsiteReview(editingItem.id, formData);
        toast.success('Review updated');
      } else {
        await addWebsiteReview(formData);
        toast.success('Review added');
      }
      setShowModal(false);
      mutate();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await deleteWebsiteReview(id);
        toast.success('Review deleted');
        mutate();
      } catch (error) {
        toast.error('Delete failed');
      }
    }
  };

  if (isLoading) return <div>Loading reviews...</div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => openModal()} className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-xl flex items-center gap-2">
          <IconPlus size={18} /> Add Review
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews?.map(review => (
          <div key={review.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative">
            <div className="absolute top-4 right-4 flex gap-1">
              <button onClick={() => openModal(review)} className="text-blue-500 hover:bg-blue-50 p-1 rounded"><IconEdit size={16} /></button>
              <button onClick={() => handleDelete(review.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><IconTrash size={16} /></button>
            </div>
            
            <div className="flex gap-1 text-yellow-400 mb-3">
              {[...Array(5)].map((_, i) => (
                <IconStar key={i} size={16} className={i < review.rating ? 'fill-current' : 'text-gray-200'} />
              ))}
            </div>
            
            <p className="text-gray-600 italic mb-6">"{review.comment}"</p>
            
            <div className="flex items-center gap-3">
              {review.customer_photo_url ? (
                <img src={googleDriveToDirectUrl(review.customer_photo_url)} alt={review.customer_name} className="w-10 h-10 rounded-full object-cover" onError={(e) => {e.target.src = 'https://ui-avatars.com/api/?name=' + review.customer_name}} />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                  {review.customer_name.charAt(0)}
                </div>
              )}
              <div>
                <h4 className="font-bold text-gray-800 text-sm">{review.customer_name}</h4>
                <p className="text-xs text-gray-500">{review.designation}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Edit Review' : 'Add Review'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input type="text" value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation / Company</label>
                  <input type="text" value={formData.designation} onChange={(e) => setFormData({...formData, designation: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rating (1-5)</label>
                  <input type="number" min="1" max="5" value={formData.rating} onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Photo URL (Google Drive Link)</label>
                  <input type="text" value={formData.customer_photo_url} onChange={(e) => setFormData({...formData, customer_photo_url: e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
                  <textarea value={formData.comment} onChange={(e) => setFormData({...formData, comment: e.target.value})} rows="3" className="w-full border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-green-500" required></textarea>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})} className="rounded text-green-600 focus:ring-green-500 h-4 w-4" />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
